import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { ComboBox, ComboBoxPropertyValues, IComboBox } from "./ComboBox";
import { NAMESPACE } from "./_Namespace";

export type ComboBoxDataServicePropertyValues =
    ComboBoxPropertyValues
    & {
        dataServiceId: string;
        dataServiceData: string;
        dataServiceOperation: string;
    };

export type ComboBoxDataServiceProperties = {
    [name in keyof ComboBoxDataServicePropertyValues]: Property<ComboBoxDataServicePropertyValues[name]>
};

export interface IComboBoxDataService extends IComboBox, ComboBoxDataServiceProperties
{
}

export class ComboBoxDataService extends ComboBox implements IComboBoxDataService
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ComboBoxDataService";
    public static override className = "ComboBoxDataService";

    // #endregion

    // #region Instance

    // #region Properties

    public dataServiceId!: PropertyString;
    public dataServiceData!: PropertyString;
    public dataServiceOperation!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ComboBoxDataServicePropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public async loadDataAsync()
    {
        const
            dataServiceId = this.dataServiceId.get();

        if (dataServiceId)
        {
            const
                dataServiceData = this.dataServiceData.get(),
                data = await ServiceManager.getRequired(WebApiClientService)
                    .createApiMethodRequest("DataService", "CallDataService", { id: dataServiceId, operation: this.dataServiceOperation.get(), data: dataServiceData })
                    .executeAsync();

            this.listDataSource.set(data.map((item: any) => ({ value: item.id, description: item.description })));
            this.triggerHandler("listDataSourceChanged");
        }

        return Promise.resolve();
    }

    // #endregion

    // #region Protected Methods

    protected _dataServiceDataChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataServiceIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataServiceOperationChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ComboBoxDataServicePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.dataServiceId =
            new PropertyString(
                this,
                propertyValues.dataServiceId,
                this._dataServiceIdChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Data" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Data Service Id" }),
                ]
            );

        this.dataServiceData =
            new PropertyString(
                this,
                propertyValues.dataServiceData,
                this._dataServiceDataChanged,
                undefined,
                [
                    new ClientAwareCategoryAttribute({ category: "Data" }),
                    new ClientAwareDisplayNameAttribute({ displayName: "Data Service Data" }),
                ]
            );

        this.dataServiceOperation = new PropertyString(this, propertyValues.dataServiceOperation, this._dataServiceOperationChanged);
    }

    // #endregion

    // #endregion
}

